import React, { useMemo } from "react";

import { IUsageCost } from "@formant/core/model/IUsageCost";

import { formatCentsToDollarString } from "../../../utils/formatCentsToDollarString";
import { DataTable } from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { RowTitle } from "../../Settings/RowTitle";
import * as styles from "./index.scss";

enum UsageTableData {
    INGESTION = "Ingestion",
    QUERY = "Queries",
    STORAGE = "Storage",
    TOTAL = "Total this period",
}
function getUsageIcon(name: UsageTableData) {
    let icon: string;
    switch (name) {
        case UsageTableData.INGESTION:
            icon = "ingestion-usage-icon";
            break;

        case UsageTableData.QUERY:
            icon = "queries-usage-icon";
            break;

        case UsageTableData.STORAGE:
            icon = "storage-usage-icon";
            break;

        default:
            icon = "total-usage-icon";
            break;
    }
    return icon;
}

export interface ITableData {
    name: UsageTableData;
    amount: string;
}

const columns: IDataTableColumn<ITableData>[] = [
    {
        header: "",
        row: _ => (
            <RowTitle
                className={styles["table-row"]}
                title={_.name}
                icon={<div className={styles[getUsageIcon(_.name)]} />}
            />
        ),
        sortBy: _ => _.name,
    },
    {
        header: "",
        row: _ => (
            <RowTitle
                className={styles["table-row"]}
                title={_.amount}
                icon={null}
            />
        ),
        sortBy: _ => _.amount,
    },
];

interface IUsageTableProps {
    usageData: IUsageCost | undefined;
}

export function UsageTable({ usageData }: IUsageTableProps) {
    const chartData = useMemo(
        () => getUsageTableData(usageData),
        [usageData, getUsageTableData],
    );
    return (
        <DataTable
            rows={[
                {
                    name: UsageTableData.INGESTION,
                    amount:
                        usageData && chartData[0] > 0
                            ? formatCentsToDollarString(chartData[0])
                            : "No usage data",
                },
                {
                    name: UsageTableData.QUERY,
                    amount:
                        usageData && chartData[1] > 0
                            ? formatCentsToDollarString(chartData[1])
                            : "No usage data",
                },
                {
                    name: UsageTableData.STORAGE,
                    amount:
                        usageData && chartData[2] > 0
                            ? formatCentsToDollarString(chartData[2])
                            : "No usage data",
                },
                {
                    name: UsageTableData.TOTAL,
                    amount:
                        usageData && chartData[3] > 0
                            ? formatCentsToDollarString(chartData[3])
                            : "No usage data",
                },
            ]}
            columns={columns}
            loading={false}
            noResultsText=""
        />
    );
}

function getUsageTableData(usageData: IUsageCost | undefined) {
    let ingestionCost = 0;
    let queryCost = 0;
    let storageCost = 0;
    let totalCost = 0;

    if (usageData && usageData?.costItems.length > 0) {
        usageData.costItems.forEach(current => {
            if (current.type === "data-in-bytes") {
                // ingestion
                ingestionCost += current.costUsdCents;
                totalCost += current.costUsdCents;
            } else if (current.type === "data-out-bytes") {
                // query
                queryCost += current.costUsdCents;
                totalCost += current.costUsdCents;
            } else if (current.type === "data-stored-bytes") {
                // storage
                storageCost += current.costUsdCents;
                totalCost += current.costUsdCents;
            } else if (current.type === "data-in-count") {
                // ingestion datapoint count
                ingestionCost += current.costUsdCents;
                totalCost += current.costUsdCents;
            }
        });
    }

    return [ingestionCost, queryCost, storageCost, totalCost];
}
